<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { blogCategoryTitle, blogHomePath, type BlogLocale } from '~/constants/blog'
import type { SofaxCrumb } from '~/components/sofax/Breadcrumb.vue'
import type { BlogPostListItem } from '~/types'

/**
 * Blog listing, ported from the template's blog page: dark title band, then a
 * two-column grid with the article cards and pagination on the left and the
 * widget sidebar on the right. Shared by /blog, /blog/page/N and the category
 * routes; all data and SEO come from useBlogListing in the page components.
 */
const props = defineProps<{
  posts: BlogPostListItem[]
  page: number
  pageCount: number
  basePath: string
  category: string | null
  searchQuery: string
  /** Which blog: the English one (default), the Czech or the German one. */
  locale?: BlogLocale
}>()

const emit = defineEmits<{ search: [term: string] }>()

const { t } = useI18n()

const heading = computed(() =>
  props.category ? t('blog.categoryTitle', { category: blogCategoryTitle(props.category) }) : t('blog.title'),
)

// The page number stays in the <h1> as before, so page 2 does not claim page 1's title.
const title = computed(() => (props.page > 1 ? `${heading.value} — ${t('blog.pageN', { page: props.page })}` : heading.value))

/*
 * Visible trail: Home (added by the component) › Blog › [category] › [Page N].
 * The JSON-LD BreadcrumbList in useBlogListing is built from the same names.
 */
const crumbs = computed<SofaxCrumb[]>(() => {
  const items: SofaxCrumb[] = [{ name: t('footer.blog'), to: blogHomePath(props.locale) }]
  if (props.category) {
    items.push({ name: blogCategoryTitle(props.category) ?? props.category, to: props.basePath })
  }
  if (props.page > 1) {
    items.push({ name: t('blog.pageN', { page: props.page }) })
  }
  return items
})

/* The sidebar's "Recent posts" reuses the current page's first three articles: no second request. */
const recentPosts = computed(() => props.posts.slice(0, 3))
</script>

<template>
  <div>
    <SofaxBreadcrumb :title="title" :subtitle="t('blog.subtitle')" :items="crumbs" />

    <section class="section-space">
      <div class="shell">
        <div class="grid grid-cols-1 gap-[1.875rem] lg:grid-cols-[1fr_minmax(416px,0.5fr)]">
          <div>
            <!-- Empty state -->
            <div
              v-if="posts.length === 0"
              class="rounded-[1.25rem] border border-line px-6 py-16 text-center dark:border-gray-700"
            >
              <i class="bi bi-journal-text mb-5 block text-5xl text-muted dark:text-gray-500" aria-hidden="true" />
              <p class="display-heading display-heading-4 mb-3">
                {{ searchQuery ? t('blog.noPostsFound') : t('blog.noPosts') }}
              </p>
              <p class="text-base">
                {{ searchQuery ? t('blog.noPostsFoundDescription') : t('blog.noPostsDescription') }}
              </p>
            </div>

            <!-- Article list -->
            <ul v-else class="grid grid-cols-1 gap-y-[1.875rem]">
              <li v-for="post in posts" :key="post.id">
                <BlogCard :post="post" :max-tags="3" layout="horizontal" />
              </li>
            </ul>

            <BlogPagination :page="page" :page-count="pageCount" :base-path="basePath" />
          </div>

          <BlogSidebar
            :recent-posts="recentPosts"
            :category="category"
            :search-query="searchQuery"
            :locale="locale"
            live-search
            @search="emit('search', $event)"
          />
        </div>
      </div>
    </section>

    <SofaxPageCta />
  </div>
</template>
